import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDataFeedInput } from '../utils/chart.util';
import { trpc } from '@client-trpc';
import { ChartState, useChartState } from '@client/state/chart-state.hook';
import { IndicatorUpdateType } from '../services/indicator-update.service';
import { ChartUpdateStream } from '../services/chart-update-stream.service';
import { DataFeedStreamMessage } from '@server/data-feed/delta/processor';

export type ChartFeed = {
  loading$: ReturnType<ChartUpdateStream['getLoading$']>;
};

type UseChartFeedParams = ChartState & {
  chartUpdateStream: ChartUpdateStream;
};

export const useChartFeed = ({
  formState,
  id,
  chartUpdateStream
}: UseChartFeedParams): ChartFeed => {
  const { getAction$ } = useChartState();
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(true);
  // Last accepted server sequence for current subscription stream.
  const lastSeqRef = useRef<number | null>(null);
  // Guard against scheduling multiple reconnect cycles at once.
  const reconnectingRef = useRef(false);
  const loading$ = useMemo(
    () => chartUpdateStream.getLoading$(),
    [chartUpdateStream]
  );
  const feedInput = useMemo(
    () => createDataFeedInput(formState, id),
    [formState, id]
  );

  const emitFeedUpdate = useCallback(
    ({ type, data }: DataFeedStreamMessage) => {
      // Step 2: backend sends explicit type ('snapshot' | 'delta').
      const updateType: IndicatorUpdateType = type;
      chartUpdateStream.emitFeedUpdate({
        updateType,
        data
      });
    },
    [chartUpdateStream]
  );

  const requestReconnect = useCallback(() => {
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    lastSeqRef.current = null;
    chartUpdateStream.setLoading(false);
    chartUpdateStream.reset();
    setSubscriptionEnabled(false);
    // Force a full trpc subscription teardown and re-subscribe cycle.
    // The next server message should be a fresh snapshot.
    queueMicrotask(() => {
      setSubscriptionEnabled(true);
    });
  }, [chartUpdateStream]);

  useEffect(() => {
    chartUpdateStream.reset();
    // New input/new run => restart sequence tracking from scratch.
    lastSeqRef.current = null;
    reconnectingRef.current = false;
    setSubscriptionEnabled(true);
  }, [chartUpdateStream, feedInput]);

  trpc.dataFeed.getFeed.useSubscription(feedInput, {
    enabled: subscriptionEnabled,
    onStarted: () => {
      reconnectingRef.current = false;
      chartUpdateStream.setLoading(true);
    },
    onData: (message) => {
      const lastSeq = lastSeqRef.current;
      const expectedNext = lastSeq == null ? null : lastSeq + 1;

      if (message.type === 'snapshot') {
        // Snapshot always re-seeds client state and sequence baseline.
        lastSeqRef.current = message.seq;
        emitFeedUpdate(message);
        chartUpdateStream.setLoading(false);
        return;
      }

      // Delta without preceding snapshot or with a sequence gap means the
      // client state can be incomplete; force fresh subscription snapshot.
      if (expectedNext == null || message.seq !== expectedNext) {
        requestReconnect();
        return;
      }

      lastSeqRef.current = message.seq;
      emitFeedUpdate(message);
      chartUpdateStream.setLoading(false);
    },
    onError: (err) => {
      console.error('Subscription error:', err);
      chartUpdateStream.setLoading(false);
    }
  });

  useEffect(() => {
    const subscription = getAction$('UPDATE_CHART').subscribe((event) => {
      if (event.payload.id !== id) return;
      requestReconnect();
    });

    return () => subscription.unsubscribe();
  }, [getAction$, id, requestReconnect]);

  useEffect(() => {
    return () => {
      chartUpdateStream.setLoading(false);
      lastSeqRef.current = null;
      reconnectingRef.current = false;
    };
  }, [chartUpdateStream]);

  return { loading$ };
};
